import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { fetchMOExclusionTypes, applyMOExclusionCascade } from '@/lib/api';

/**
 * MO Exclusion Cascade — admin-only dialog.
 *
 * Lets an admin pick a manner/cause-of-death exclusion class
 * (vehicular, gunshot, sharp-force, suicide, natural, accidental)
 * and cascade-strip every serial-killer perpetrator flag whose
 * documented MO is incompatible with that class. The backend writes a
 * single unified investigator note + adds a MOD_EXCLUSION_* marker.
 *
 * Server endpoint: POST /api/admin/cases/:case_id/mo-exclusion
 */
export default function MOExclusionDialog({
  open, onOpenChange, caseId, caseLabel, onApplied,
}) {
  const [types, setTypes] = useState([]);
  const [exclusionType, setExclusionType] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setExclusionType('');
    setLoading(true);
    fetchMOExclusionTypes()
      .then((data) => setTypes(data.types || []))
      .catch(() => toast.error('Failed to load exclusion types'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleApply = async () => {
    if (!exclusionType) {
      toast.error('Select an exclusion type');
      return;
    }
    setSubmitting(true);
    try {
      const res = await applyMOExclusionCascade(caseId, exclusionType);
      setResult(res);
      const n = res.stripped_count;
      toast.success(
        n > 0
          ? `Stripped ${n} perpetrator flag${n === 1 ? '' : 's'} · added ${res.marker_flag}`
          : `No incompatible flags to strip · added ${res.marker_flag}`,
      );
      if (onApplied) onApplied(res);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to apply MO exclusion';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const selected = types.find((t) => t.key === exclusionType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="mo-exclusion-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-600" />
            MO Exclusion Cascade
          </DialogTitle>
          <DialogDescription>
            Strip serial-killer perpetrator flags incompatible with this
            case&apos;s documented manner/cause of death. A unified
            investigator note will be written and a MOD_EXCLUSION_*
            marker added.
            {caseLabel && (
              <span className="block mt-1 font-mono text-xs text-muted-foreground">
                Target: {caseLabel}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-3 py-2">
            <Label htmlFor="mo-exclusion-type-select">Exclusion type</Label>
            <Select
              value={exclusionType}
              onValueChange={setExclusionType}
              disabled={loading || submitting}
            >
              <SelectTrigger
                id="mo-exclusion-type-select"
                data-testid="mo-exclusion-type-select"
              >
                <SelectValue placeholder={loading ? 'Loading…' : 'Choose type…'} />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem
                    key={t.key}
                    value={t.key}
                    data-testid={`mo-exclusion-option-${t.key}`}
                  >
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <p className="text-xs text-muted-foreground">
                Will add marker: <span className="font-mono">{selected.marker_flag}</span>
              </p>
            )}
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/50 p-2 text-xs text-amber-900 dark:text-amber-200">
              The cascade strips perpetrator flags whose documented MO is
              incompatible with the chosen exclusion. Flags from mixed-MO
              perpetrators are retained when at least one method survives
              the exclusion.
            </div>
          </div>
        )}

        {result && (
          <div
            className="space-y-2 py-2 text-sm"
            data-testid="mo-exclusion-result"
          >
            <div>
              <span className="font-semibold">Marker added:</span>{' '}
              <span className="font-mono text-xs">{result.marker_flag}</span>
            </div>
            <div>
              <span className="font-semibold">Stripped {result.stripped_count} flag(s):</span>
              {result.stripped_flags && result.stripped_flags.length > 0 ? (
                <ul className="list-disc pl-5 mt-1 font-mono text-xs">
                  {result.stripped_flags.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              ) : (
                <span className="ml-2 text-muted-foreground">none</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground italic mt-2">
              {result.rationale}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="mo-exclusion-close-btn"
          >
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleApply}
              disabled={!exclusionType || loading || submitting}
              data-testid="mo-exclusion-apply-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Apply Cascade
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
